import React, { PropsWithChildren } from 'react'

import { XIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { Popup } from 'react-map-gl/mapbox'

import {
  IGeoJson2DGeographicCoordinates,
  ISiteLocation,
} from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'

import SitemapAttributeTag from '../SitemapAttributeTag/SitemapAttributeTag'
import MapViewItemBase from '../SitemapHelpers/models/MapViewItemBase'

// mapbox's own content wrapper paints a white 3px-radius shadowed box behind
// the card — neutralize it entirely (cf. ResourceCardList's popups) so the
// card owns background, radius and shadow. `!` because mapbox-gl.css is
// unlayered and beats layered utilities on the same property.
const VENDOR_CONTENT_CLASSES = [
  'tw:[&_.mapboxgl-popup-content]:p-0!',
  'tw:[&_.mapboxgl-popup-content]:bg-transparent!',
  'tw:[&_.mapboxgl-popup-content]:shadow-none!',
  'tw:[&_.mapboxgl-popup-content]:rounded-none!',
].join(' ')

// The card owns a 1px border; the default mapbox tip sits OUTSIDE it, so the
// border runs as a separator across the tip base. Pull the tip 1px over the
// card (stacked above it — z-index works on flex items) and color it like the
// surface it meets — popover body below the card, dark header above it — so
// card + tip read as one continuous shape. Selectors must stay quote-free
// (quoted attribute selectors break Tailwind's class extraction), and the
// z-index/color utilities need `!`: mapbox-gl.css is UNLAYERED, so it beats
// normal layered utilities on the same properties regardless of specificity.
const SEAMLESS_TIP_CLASSES = [
  'tw:[&_.mapboxgl-popup-tip]:z-[3]!',
  'tw:[&.mapboxgl-popup-anchor-bottom_.mapboxgl-popup-tip]:-mt-px',
  'tw:[&.mapboxgl-popup-anchor-bottom_.mapboxgl-popup-tip]:border-t-popover!',
  'tw:[&.mapboxgl-popup-anchor-top_.mapboxgl-popup-tip]:-mb-px',
  // Side anchors (popup flipped at a screen edge): tip meets the card's side.
  'tw:[&.mapboxgl-popup-anchor-left_.mapboxgl-popup-tip]:-mr-px',
  'tw:[&.mapboxgl-popup-anchor-left_.mapboxgl-popup-tip]:border-r-popover!',
  'tw:[&.mapboxgl-popup-anchor-right_.mapboxgl-popup-tip]:-ml-px',
  'tw:[&.mapboxgl-popup-anchor-right_.mapboxgl-popup-tip]:border-l-popover!',
  // Corner anchors have no diagonal tip geometry — a cardinal tip there dangles
  // off the card into the margin, so hide it (the card just meets the corner).
  'tw:[&.mapboxgl-popup-anchor-top-left_.mapboxgl-popup-tip]:hidden',
  'tw:[&.mapboxgl-popup-anchor-top-right_.mapboxgl-popup-tip]:hidden',
  'tw:[&.mapboxgl-popup-anchor-bottom-left_.mapboxgl-popup-tip]:hidden',
  'tw:[&.mapboxgl-popup-anchor-bottom-right_.mapboxgl-popup-tip]:hidden',
].join(' ')

// The tip above the card meets the HEADER, so it takes the header's colour — grey for a shared
// entity, the dark band otherwise. Unlayered mapbox-gl.css again, hence `!`.
const HEADER_TIP_CLASS = {
  dark: 'tw:[&.mapboxgl-popup-anchor-top_.mapboxgl-popup-tip]:border-b-foreground!',
  shared:
    'tw:[&.mapboxgl-popup-anchor-top_.mapboxgl-popup-tip]:border-b-muted-foreground!',
  // A custom header has no band of its own, so the tip meets the card.
  custom:
    'tw:[&.mapboxgl-popup-anchor-top_.mapboxgl-popup-tip]:border-b-popover!',
}

// The pin stands UP from its coordinate, so only a popup above it has to clear anything. Mapbox
// applies a bare number in whichever direction it flips, which pushed a flipped-below popup the
// full pin height away from a pin that was never in its way. Per-anchor offsets say it properly:
// the clearance upwards, a tip gap everywhere else.
const POPUP_TIP_GAP_PX = 8
const pinAwareOffset = (
  clearancePx: number,
): Record<string, [number, number]> => ({
  bottom: [0, -clearancePx],
  'bottom-left': [0, -clearancePx],
  'bottom-right': [0, -clearancePx],
  top: [0, POPUP_TIP_GAP_PX],
  'top-left': [0, POPUP_TIP_GAP_PX],
  'top-right': [0, POPUP_TIP_GAP_PX],
  left: [POPUP_TIP_GAP_PX, 0],
  right: [-POPUP_TIP_GAP_PX, 0],
})

interface IProps {
  item: MapViewItemBase
  sitemapLocation?: ISiteLocation
  coordOverride?: IGeoJson2DGeographicCoordinates
  // Lift the popup further from its anchor (px) so its tip clears a marker/label
  // sitting at the same coordinate. Defaults to the Popup's standard offset. Applied only on the
  // side the pin occupies — see pinAwareOffset.
  offset?: number
  // The anchor is centred on its coordinate, like a location marker, so the clearance applies in
  // every direction: mapbox's own reading of a bare number.
  isOffsetCentred?: boolean
  // Names the header when `item` can't: `item` is the nearest DISPLAYED ancestor
  // of the workflow's `locations`, so it is absent for an equipment-only workflow
  // and while the map shows only the project marker. Header-only — never positional.
  headerLocation?: LocationBase

  // Names the project a shared entity came from. Present only for shared entities, and it
  // switches the header to the grey band that matches the shared pins — a shared workflow has no
  // location of this project's to name, and the dark band read as a local one.
  sharedFromProjectName?: string

  onClose(): void
  // Mapbox closes on any map click, including the click that re-picks the very thing this
  // popup is describing — which deselects and reselects in one gesture. False hands
  // deselection to the map's own miss path instead.
  closeOnMapClick?: boolean
  // False leaves focus where it is: mapbox otherwise focuses the first control it finds.
  focusAfterOpen?: boolean
  customHeader?: JSX.Element
}

@observer
export default class GeneralMapPopup extends React.Component<
  PropsWithChildren<IProps>
> {
  public render() {
    const {
      item,
      onClose,
      children,
      customHeader,
      sitemapLocation,
      coordOverride,
      offset,
      isOffsetCentred,
      sharedFromProjectName,
      closeOnMapClick = true,
    } = this.props

    const longitude =
      coordOverride?.longitude ??
      sitemapLocation?.preciseLocation?.longitude ??
      item?.sitemapItem?.coordinates?.longitude
    const latitude =
      coordOverride?.latitude ??
      sitemapLocation?.preciseLocation?.latitude ??
      item?.sitemapItem?.coordinates?.latitude

    // A selection can outlive its pin's coordinate — pins hidden, filtered off this map, or a
    // project catch-all anchor that has not resolved yet — and mapbox's LngLat throws on undefined.
    if (longitude == null || latitude == null) {
      return null
    }

    return (
      <Popup
        longitude={longitude}
        latitude={latitude}
        offset={
          offset == null || isOffsetCentred
            ? offset
            : (pinAwareOffset(offset) as never)
        }
        closeOnClick={closeOnMapClick}
        className={`tw:max-w-fit ${VENDOR_CONTENT_CLASSES} ${SEAMLESS_TIP_CLASSES} ${
          customHeader
            ? HEADER_TIP_CLASS.custom
            : sharedFromProjectName
              ? HEADER_TIP_CLASS.shared
              : HEADER_TIP_CLASS.dark
        }`}
        onClose={onClose}
        closeButton={false}
        focusAfterOpen={this.props.focusAfterOpen}
      >
        {/* Keep the 1px border — the seamless tip's -1px overlap is calibrated
            to it; overflow-hidden clips the dark header's top corners. */}
        <div className="tw:relative tw:z-[2] tw:flex tw:max-h-[332px] tw:min-h-[50px] tw:w-fit tw:max-w-[395px] tw:flex-col tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:shadow-md tw:[&_*]:outline-none!">
          {customHeader || this.renderHeader()}
          {children}
        </div>
      </Popup>
    )
  }

  private renderHeader() {
    const { item, onClose, headerLocation, sharedFromProjectName } = this.props
    if (sharedFromProjectName) {
      return this.renderSharedHeader(sharedFromProjectName)
    }
    // Keyed on `item` itself, not `item.dataObject`: a text-box item carries a
    // displayName with no dataObject, and must keep naming itself.
    const dataObject = item ? item.dataObject : headerLocation
    const name = item ? item.displayName : headerLocation?.name
    return (
      <header className="tw:flex tw:min-h-[30px] tw:items-center tw:gap-2 tw:bg-foreground tw:px-3 tw:text-body-md tw:font-semibold tw:text-background tw:[&_.hierarchy-delivery-attribute-tag-content]:text-background">
        {/* Ghost hover tokens are calibrated for light surfaces — override
            them for the dark band. */}
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:-ml-1 tw:shrink-0 tw:text-background tw:hover:bg-background/10 tw:hover:text-background"
          onClick={onClose}
          aria-label={Localization.translator.close}
        >
          <XIcon aria-hidden="true" className="tw:size-4" />
        </Button>
        {/* Equipment names run long — ellipsize rather than push the X out. */}
        <SitemapAttributeTag
          shouldShowAsTag={false}
          dataObject={dataObject}
          className="tw:min-w-0"
          contentContainerClassName="tw:truncate"
        >
          <span>{name}</span>
        </SitemapAttributeTag>
      </header>
    )
  }
  // Same band the shared entity's own side panel shows: the mark, the label and the project
  // name it came from.
  private renderSharedHeader(projectName: string) {
    return (
      <header className="tw:flex tw:min-h-[30px] tw:items-center tw:gap-2 tw:bg-muted-foreground tw:px-3 tw:text-body-md tw:font-semibold tw:text-background">
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:-ml-1 tw:shrink-0 tw:text-background tw:hover:bg-background/10 tw:hover:text-background"
          onClick={this.props.onClose}
          aria-label={Localization.translator.close}
        >
          <XIcon aria-hidden="true" className="tw:size-4" />
        </Button>
        <span className="tw:truncate">
          {Localization.translator.sharedFrom}{' '}
          <span className="tw:underline">{projectName}</span>
        </span>
      </header>
    )
  }
}

// A popup that lists several cards rules between them: the separator is the list's business, not
// the card's — PermitListItem is shared with five other surfaces. divide-y only draws BETWEEN
// children, so a single-item popup and the detail-block popups stay unruled.
export const MapPopupBody: React.FC = ({ children }) => (
  <div className="tw:relative tw:max-h-[300px] tw:w-[375px] tw:divide-y tw:divide-border tw:overflow-auto tw:px-2">
    {children}
  </div>
)
